import { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  Box,
  Snackbar,
  IconButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINT } from '../config';
import { Helmet } from 'react-helmet';

const PasswordGenerator = () => {
  const [length, setLength] = useState('');
  const [complexity, setComplexity] = useState('1');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPassword('');
    setError('');

    try {
      if (!length || !complexity) {
        throw new Error("Invalid input...");
      }
      const response = await fetch(API_ENDPOINT + 'servertools/password-genrate?length=' + length + '&hard=' + complexity);
      setPassword(await response.text());
    } catch (err) {
      console.error(err);
      setError('Error generating password');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(password).then(() => {
      setCopySuccess(true);
    });
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5 }}>
      <Helmet>
        <title>Password Generator | Redmorus</title>
        <meta name="description" content="Generate secure passwords instantly with adjustable length and complexity levels." />
        <meta name="keywords" content="password generator, secure password, random password, Redmorus" />
      </Helmet>
      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
        <motion.div
          whileHover={{ x: -5 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <IconButton onClick={() => navigate('/')} color="primary">
            <ArrowBackIcon />
          </IconButton>
        </motion.div>
        <Typography variant="h6" sx={{ ml: 1 }}>
          Back
        </Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <Box
          component="img"
          src="/logo64.png"
          alt="Redmorus Logo"
          sx={{ width: 20, height: 20, mr: 1 }}
        />
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Redmorus
        </Typography>
      </Box>
      <Typography variant="h4" gutterBottom>
        Password Generator
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
        <TextField
          label="Length"
          type="number"
          value={length}
          onChange={(e) => setLength(e.target.value)}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          label="Complexity"
          select
          value={complexity}
          onChange={(e) => setComplexity(e.target.value)}
          fullWidth
          required
          margin="normal"
        >
          <MenuItem value="1">Simple</MenuItem>
          <MenuItem value="2">Medium</MenuItem>
          <MenuItem value="3">Hard</MenuItem>
        </TextField>
        <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
          Submit
        </Button>
      </Box>

      {password && (
        <Alert severity="success" sx={{ mt: 3 }} action={
          <Button
            size="small"
            variant="outlined"
            onClick={handleCopy}
            startIcon={<ContentCopyIcon />}
          >
            Copy
          </Button>
        }>
          Generated Password: <strong style={{ marginLeft: '8px' }}>{password}</strong>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Password copied to clipboard"
      />
    </Container>
  );
};

export default PasswordGenerator;
